import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function WeaveRoboticsIsaacRobotButlerPage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: 'Weave Robotics Isaac 家庭用ロボットバトラー発表' }
  ];

  const article = {
    category: 'テクノロジー・未来トレンド',
    date: '2024年9月9日',
    title: 'Weave Robotics Isaac 家庭用ロボットバトラー発表',
    subtitle: '59,000ドルの家事自動化ロボットが示す執事サービスの未来',
    heroImage: '/images/generated/jba-fs-139-news_weave-robotics-isaac-robot-butler.webp',
    lead: 'Weave Roboticsが発表した家庭用ロボット「Isaac」は、59,000ドルという価格設定で様々な家事を自律的に実行する多用途パーソナルロボットです。2025年秋に初期30ユニットの出荷が予定されており、執事サービスの概念が具体的な商用製品として実現されつつあることを示しています。',
    comment: 'Weave Roboticsの「Isaac」発表は、ロボット技術が「バトラー」という概念を採用していることの文化的意義を示す重要な事例です。約885万円という価格設定は、執事サービスがプレミアムサービスとして認識されていることを技術分野でも証明しています。しかし、技術が代替できる家事タスクと、人間の執事が提供する感情的知性、文化的理解、柔軟な対応には明確な違いがあります。ロボット技術の発展は、むしろ人間の執事の不可欠性—パーソナライゼーション、信頼関係の構築、複雑な状況への適応—を浮き彫りにするものと考えております。テクノロジーと伝統的執事サービスは相補的関係にあり、日本市場においても人的サービスの価値を再評価する機会として捉え、より高度な執事サービスの確立に取り組んでまいります。',
    related: [
      { to: '/news/apple-robot-butler', image: '/images/generated/jba-fs-144-news_weave-robotics-isaac-robot-butler-v4.webp', aspect: 'aspect-[3/4]', alt: 'Apple社ロボットバトラー開発プロジェクト始動の関連記事', title: 'Apple社ロボットバトラー開発プロジェクト始動', desc: '世界最大級のテクノロジー企業の執事ロボット開発' },
      { to: '/news/ai-hospitality-revolution', image: '/images/generated/jba-fs-145-news_weave-robotics-isaac-robot-butler-v3.webp', aspect: 'aspect-[3/4]', alt: 'AIホスピタリティ革命：5つの変革の関連記事', title: 'AIホスピタリティ革命：5つの変革', desc: '人工知能が切り開く執事サービスの未来' }
    ]
  };

  const sources = [
    { label: '出典元', value: 'The Rundown AI', link: false },
    { label: '記事URL', value: 'https://www.therundown.ai/p/futuristic-robot-butlers-weave-2025', link: true },
    { label: '価格・出荷（Isaac 1）', value: 'https://thenextweb.com/news/weave-robotics-isaac-1-home-robot', link: true },
    { label: 'メーカー公式サイト', value: 'https://www.weaverobotics.com/', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];

  const toc = [
    { id: 's-product', label: '製品概要と主要機能' },
    { id: 's-company', label: '開発企業ウィーブ・ロボティクスの背景' },
    { id: 's-pricing', label: '価格設定と市場戦略' },
    { id: 's-competition', label: '競合状況と市場の加熱' },
    { id: 's-isaac0', label: '先行モデルIsaac 0とその後の展開' },
    { id: 's-realization', label: '執事サービス概念の技術的実現' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Technology" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                'Weaveの家庭用ロボット「Isaac」は、片付け・洗濯物畳み・整理などを自律的に実行する多用途パーソナルロボット。音声・テキスト・アプリで操作できる',
                '発表時の価格は59,000ドル（約885万円）。予約金1,000ドル、初期ロットは米国30人向けで、2025年秋の出荷が予定されていた',
                'Tesla Optimus、1X NEO Beta、中国AGIBOTなど競合が相次いで参入し、家庭用ロボットバトラー市場が加熱している'
              ]}
            />

            {/* 01: 製品概要（既存本文・一字も削らず。3段落の途中に出典スクショを挟み壁を回避） */}
            <SectionHeading id="s-product" en="Product">製品概要と主要機能</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Weaveが発表した「Isaac」は、カリフォルニアで製造される多用途パーソナルロボットで、様々な家事を手伝うために設計されています。このロボットは自律的に片付け、洗濯物を畳み、スペースを整理するなど、多くの家事を実行することができます。</p>
              <p className={P}>特に注目すべき機能として、ペットに餌を与えることや散らかった部屋を片付けることも可能で、これらの作業はすべて所有者が外出している間でも実行できます。これは、従来の執事サービスが提供していた「不在時の家庭管理」という概念を技術的に実現したものです。</p>
            </div>
            <SourceShot
              src="/images/news-sources/weave-robotics-isaac-robot-butler-therundown.jpg"
              alt="The Rundown AIに掲載されたWeave Roboticsの家庭用ロボットバトラーIsaacを報じる記事のスクリーンショット"
              media="The Rundown AI"
              url="https://www.therundown.ai/p/futuristic-robot-butlers-weave-2025"
            />
            <div className="space-y-6">
              <p className={P}>操作方法は音声またはテキストコマンドに応答し、アプリ経由でプログラミングも可能です。プライバシーへの配慮として、Isaacが持ち歩くカメラは使用していないときは折りたたんで付属のケースに収納できる設計となっています。</p>
            </div>

            {/* 増補: 開発企業の背景 */}
            <SectionHeading id="s-company" en="The Company">開発企業ウィーブ・ロボティクスの背景</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Weave Roboticsは、Y Combinator出身のEvanとKaanによって設立されたスタートアップで、実用的な家庭用ロボットの開発を手がけています。同社は「自分たちの家庭のために作ったロボット」として製品を位置づけています。</p>
              <p className={P}>創業者自身が生活の現場で使うことを前提に設計している点が、実験室的なプロトタイプではなく、日常の家事に耐える実用機を志向する同社の姿勢を表しています。</p>
            </div>

            {/* 02: 価格設定（既存本文・一字も削らず。冒頭に象徴句を挟み壁を回避） */}
            <SectionHeading id="s-pricing" en="Pricing">価格設定と市場戦略</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Isaacの価格設定は59,000ドル（約885万円）という高額設定となっており、これは分割払いで月額1,385ドル×48ヶ月での購入も可能です。予約金は1,000ドルで、初期ロットは米国の30人の顧客向けに限定されています。</p>
            </div>
            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="59,000ドルの家事自動化ロボットが示す、執事サービスの未来。" cite="Weave Robotics Isaac" />
            </div>
            <div className="space-y-6">
              <p className={P}>この価格設定は、ロボットバトラーがプレミアムサービスとして位置づけられていることを明確に示しています。約885万円という価格は、年収の高い専門職の執事を数年間雇用するのと同等の投資額であり、技術による執事サービスの代替が高付加価値市場をターゲットにしていることが分かります。</p>
              <p className={P}>2025年秋に最初の顧客への出荷が予定されており、限定的な初期展開から始まることで、製品の品質と顧客満足度を確保しながら市場投入を進める戦略が取られています。</p>
            </div>

            {/* 03: 競合状況（既存本文・一字も削らず。要点ボックスで壁を回避） */}
            <SectionHeading id="s-competition" en="Competition">競合状況と市場の加熱</SectionHeading>
            <div className="space-y-6">
              <p className={P}>手頃な価格でインテリジェントなパーソナルロボットアシスタントの競争は加熱し続けており、2025年までにこれらのロボットが家庭で見られるようになることが予想されています。主要な競合企業には以下が含まれます。</p>
              <p className={P}>Tesla Optimusは、電気自動車で知られるTeslaが開発する人型ロボットで、製造業から家庭用途まで幅広い応用を目指しています。1X NEO Betaは、ノルウェーの企業が開発する人型ロボットで、より人間に近い動作を特徴としています。</p>
            </div>
            <div className="my-6">
              <KeyPointsBox
                title="主要な競合ロボット"
                points={[
                  'Tesla Optimus — 電気自動車で知られるTeslaの人型ロボット。製造業から家庭用途まで幅広い応用を目指す',
                  '1X NEO Beta — ノルウェー企業が開発する人型ロボット。より人間に近い動作を特徴とする',
                  'AGIBOT（中国）— 同様の市場に参入し、アジア市場での展開を進める'
                ]}
              />
            </div>
            <div className="space-y-6">
              <p className={P}>中国のAGIBOTも同様の市場に参入しており、アジア市場での展開を進めています。これらの企業が同時に家庭用ロボットバトラー市場に参入していることは、この分野が単なる未来の夢ではなく、実現間近の現実であることを示しています。</p>
            </div>

            {/* 増補: 先行モデルIsaac 0とその後の展開 */}
            <SectionHeading id="s-isaac0" en="Isaac 0">先行モデルIsaac 0とその後の展開</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Weaveはその後、据置型で洗濯物を畳む先行モデル「Isaac 0」をカリフォルニアで出荷開始しました。公式サイトによれば、Isaac 0は週<Stat>1,000</Stat>ポンド超の洗濯物を畳み、<Stat>2,000</Stat>時間を超える実地稼働データを積み上げています。</p>
              <p className={P}>さらに同社は、車輪とアームを備えた次世代モデル「Isaac 1」を公開し、家庭向けロボットのラインナップを段階的に拡充しています。実運用データに基づく着実な製品進化が、同社のアプローチの特徴です。</p>
            </div>

            {/* 04: 技術的実現（既存本文・一字も削らず。出典スクショで壁を回避） */}
            <SectionHeading id="s-realization" en="Realization">執事サービス概念の技術的実現</SectionHeading>
            <div className="space-y-6">
              <p className={P}>この発表の最も重要な意義は、「バトラー」という名称を冠したロボットが、伝統的な執事サービスの概念—家事管理、タスクの自動化、生活の質の向上—を技術的に実現しようとする試みであることです。</p>
              <p className={P}>記事では「もう二度と洗濯物を畳まなくて済むことに興奮している」と結論づけられており、これは執事サービスの本質的な価値である「日常的な作業からの解放」を技術によって実現することへの期待を表しています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/weave-robotics-isaac-robot-butler-weaverobotics.jpg"
              alt="Weave Robotics公式サイトのトップページのスクリーンショット。現行モデルIsaac 1の紹介が掲載されている"
              media="Weave Robotics 公式サイト"
              url="https://www.weaverobotics.com/"
            />
            <div className="space-y-6">
              <p className={P}>家庭用ロボット市場が急速に現実化していることは、家事労働の未来を大きく変える可能性があります。しかし同時に、人間の執事が提供する感情的知性、文化的理解、柔軟な対応といった要素は、技術では代替困難な価値として再認識される可能性もあります。</p>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">発表</dt><dd className="text-[#333] mt-0.5">2024年9月9日</dd></div>
                  <div><dt className="text-[#8f7a56]">メーカー</dt><dd className="text-[#333] mt-0.5">Weave Robotics（YC出身）</dd></div>
                  <div><dt className="text-[#8f7a56]">Isaac価格</dt><dd className="text-[#333] mt-0.5">59,000ドル（約885万円）</dd></div>
                  <div><dt className="text-[#8f7a56]">カテゴリ</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
